import type { Edge } from 'reactflow';
import type { CustomNode } from '../types/nodes';

/**
 * Check if a node has meaningful configuration data
 * @param node - Node to validate
 * @param isDraft - Whether this is a draft workflow (more lenient validation)
 */
export function hasValidConfiguration(node: CustomNode, isDraft: boolean = true): boolean {
	const { type, data } = node;

	// Flow control nodes don't have config property
	if (data.category === 'flow-control') {
		return true;
	}

	// Processing and integration nodes have config
	const config = (data as any).config || {};

	switch (type) {
		case 'end':
			// Flow control nodes don't need configuration
			return true;

		case 'start':
			// ❌ Start nodes are deprecated - workflows should not use them
			return false;

		case 'submission-form':
			// For draft workflows, be more lenient - only require basic structure
			if (isDraft) {
				return true; // Allow incomplete submission forms in drafts
			}
			// Must have form fields and a name for published workflows
			return Boolean(
				config.name &&
					config.name.trim() &&
					Array.isArray(config.fields) &&
					config.fields.length > 0
			);

		case 'api-call':
			// Even in draft mode, require at least some basic configuration
			// This prevents showing green checkmarks on completely unconfigured nodes
			const hasBasicConfig = Boolean(
				config.url && config.url.trim() && config.method && config.method.trim()
			);

			// For draft workflows, be lenient but still require basic structure
			if (isDraft) {
				// In draft mode, require at least URL or method to be configured
				return Boolean(
					(config.url && config.url.trim()) || (config.method && config.method.trim())
				);
			}

			// For published workflows, require full configuration
			return hasBasicConfig;

		case 'transform':
			// Must have at least one transformation with output and expression
			const hasTransformations = Boolean(
				Array.isArray(config.transformations) &&
					config.transformations.length > 0 &&
					config.transformations.some(
						(transformation: any) =>
							transformation.output &&
							transformation.output.trim() &&
							transformation.expression &&
							transformation.expression.trim()
					)
			);

			// For draft workflows, be lenient but still require basic structure
			if (isDraft) {
				// In draft mode, require at least one transformation to be started
				return Boolean(
					Array.isArray(config.transformations) &&
						config.transformations.length > 0 &&
						config.transformations.some(
							(transformation: any) =>
								transformation.output && transformation.output.trim()
						)
				);
			}

			// For published workflows, require full transformation configuration
			return hasTransformations;

		case 'condition':
			// Must have at least one valid condition
			const hasValidConditions = Boolean(
				Array.isArray(config.conditions) &&
					config.conditions.length > 0 &&
					config.conditions.some((condition: any) => {
						if (condition.type === 'simple') {
							return condition.field && condition.operator;
						} else if (condition.type === 'advanced') {
							return condition.expression && condition.expression.trim();
						}
						return false;
					})
			);

			// For draft workflows, be lenient but still require basic structure
			if (isDraft) {
				// In draft mode, require at least one condition to be started
				return Boolean(
					Array.isArray(config.conditions) &&
						config.conditions.length > 0 &&
						config.conditions.some(
							(condition: any) =>
								condition.type && (condition.field || condition.expression)
						)
				);
			}

			// For published workflows, require full condition configuration
			return hasValidConditions;

		case 'database':
			// NEW: Check for secure connection structure
			if (config.connection?.secretRef) {
				// Secure connection with secret reference
				return Boolean(config.operation);
			}

			// Legacy validation for backward compatibility
			if (isDraft) {
				return Boolean(
					config.connection?.secretRef ||
						(config.connection?.host && config.connection?.database)
				);
			}

			// For published workflows, require secure connection
			return Boolean(config.connection?.secretRef && config.operation);

		case 'email':
			// Must have from address, at least one recipient, subject, and either template or content
			return Boolean(
				config.from?.address &&
					config.to &&
					Array.isArray(config.to) &&
					config.to.length > 0 &&
					config.to.some((recipient: string) => recipient.trim()) &&
					config.subject &&
					config.subject.trim() &&
					// Either template OR htmlContent must be provided
					((config.template && config.template.trim()) ||
						(config.htmlContent && config.htmlContent.trim()))
			);

		case 'slack':
			// NEW: Support both webhook (legacy) and bot token (new) configurations
			const hasLegacyWebhook = Boolean(
				config.webhookUrl &&
					config.webhookUrl.trim() &&
					config.message &&
					config.message.trim()
			);

			const hasNewBotConfig = Boolean(
				config.credentialId &&
					config.credentialId.trim() &&
					config.channels &&
					Array.isArray(config.channels) &&
					config.channels.length > 0 &&
					config.content &&
					((config.content.text && config.content.text.trim()) ||
						(config.content.blocks && config.content.blocks.length > 0))
			);

			return hasLegacyWebhook || hasNewBotConfig;

		case 'hubspot':
			// Must have API type, operation, and response storage
			return Boolean(
				config.apiType &&
					config.apiType.trim() &&
					config.operation &&
					config.operation.trim() &&
					config.storeAs &&
					config.storeAs.trim() &&
					// For operations that need property mappings, check if they exist
					(config.operation === 'get' ||
						(config.properties && Object.keys(config.properties).length > 0))
			);

		// Advanced node validation rules
		case 'loop':
			// Must have array field to iterate over
			return Boolean(config.arrayField && config.arrayField.trim());

		case 'wait':
			// Must have either delay or resume timestamp
			return Boolean(
				(config.delayMs && config.delayMs > 0) ||
					(config.resumeAt && config.resumeAt.trim())
			);

		case 'webhook-listener':
			// Must have expected fields configured
			return Boolean(
				Array.isArray(config.expectedFields) && config.expectedFields.length > 0
			);

		case 'approval':
			// Must have approvers and message
			// Approvers can be either an array or a comma-separated string
			const approversValid =
				config.approvers &&
				((Array.isArray(config.approvers) && config.approvers.length > 0) ||
					(typeof config.approvers === 'string' && config.approvers.trim().length > 0));
			return Boolean(approversValid && config.message && config.message.trim());

		case 'logger':
			// Logger always valid (can log full context or specific fields)
			return true;

		case 'validator':
			// Must have at least one validation rule
			return Boolean(
				Array.isArray(config.rules) &&
					config.rules.length > 0 &&
					config.rules.some((rule: any) => rule.field && rule.field.trim() && rule.type)
			);

		case 'storage':
			// Must have key and value
			return Boolean(config.key && config.key.trim() && config.value && config.value.trim());

		case 'webhook-caller':
			// Must have URL and method
			const webhookValid = Boolean(config.url && config.url.trim() && config.method);
			// Debug logging disabled to prevent console spam
			// console.log('Webhook caller validation:', {
			//	config,
			//	valid: webhookValid,
			//	url: config.url,
			//	method: config.method,
			// });
			return webhookValid;

		case 'switch':
			// Must have at least one case with expression
			return Boolean(
				Array.isArray(config.cases) &&
					config.cases.length > 0 &&
					config.cases.some((case_: any) => case_.expression && case_.expression.trim())
			);

		case 'retry':
			// Must have retry count and delay
			return Boolean(
				config.retries &&
					config.retries > 0 &&
					config.delayMs &&
					config.delayMs > 0 &&
					config.strategy
			);

		case 'parallel':
			// Must have at least one branch
			return Boolean(
				Array.isArray(config.branches) &&
					config.branches.length > 0 &&
					config.branches.some((branch: any) => branch.label && branch.label.trim())
			);

		case 'stripe':
			// Must have operation and required fields for that operation
			if (!config.operation || !config.operation.trim()) {
				return false;
			}

			// For draft workflows, be more lenient - just require operation
			if (isDraft) {
				return true;
			}

			// For published workflows, require operation-specific validation
			const operation = config.operation;
			switch (operation) {
				case 'create_payment_intent':
					return Boolean(config.amountPath && config.amountPath.trim());
				case 'create_customer':
					return Boolean(config.customerEmailPath && config.customerEmailPath.trim());
				case 'create_subscription':
					return Boolean(
						config.subCustomerIdPath &&
							config.subCustomerIdPath.trim() &&
							config.priceIdPath &&
							config.priceIdPath.trim()
					);
				case 'refund_payment':
					return Boolean(
						(config.paymentIntentIdPath && config.paymentIntentIdPath.trim()) ||
							(config.chargeIdPath && config.chargeIdPath.trim())
					);
				case 'attach_payment_method':
					return Boolean(
						config.attachCustomerIdPath &&
							config.attachCustomerIdPath.trim() &&
							config.attachPaymentMethodIdPath &&
							config.attachPaymentMethodIdPath.trim()
					);
				case 'cancel_subscription':
					return Boolean(config.subscriptionIdPath && config.subscriptionIdPath.trim());
				case 'create_invoice':
					return Boolean(
						config.invoiceCustomerIdPath && config.invoiceCustomerIdPath.trim()
					);
				case 'finalize_invoice':
				case 'send_invoice':
					return Boolean(config.invoiceIdPath && config.invoiceIdPath.trim());
				default:
					return true; // Unknown operations are considered valid
			}

		default:
			// For unknown node types, assume they need some config
			return Object.keys(config).length > 0;
	}
}

/**
 * Check if a node has the required connections
 */
export function hasRequiredConnections(node: CustomNode, edges: Edge[]): boolean {
	const { id, type } = node;

	// Find incoming and outgoing connections
	const incomingEdges = edges.filter(edge => edge.target === id);
	const outgoingEdges = edges.filter(edge => edge.source === id);

	switch (type) {
		case 'start':
			// Start nodes need outgoing connections
			return outgoingEdges.length > 0;

		case 'end':
			// End nodes need incoming connections
			return incomingEdges.length > 0;

		case 'submission-form':
			// Submission form is the root node - doesn't need incoming connections
			// Can be complete with just configuration, even without outgoing connections
			return true;

		case 'api-call':
		case 'transform':
		case 'condition':
		case 'database':
		case 'email':
		case 'slack':
		case 'hubspot':
		case 'stripe':
		case 'loop':
		case 'wait':
		case 'webhook-listener':
		case 'approval':
		case 'logger':
		case 'validator':
		case 'storage':
		case 'webhook-caller':
		case 'switch':
		case 'retry':
		case 'parallel':
			// Processing, integration, and advanced nodes need incoming connections
			// Exception: they can be terminal nodes (no outgoing) if they're at the end
			return incomingEdges.length > 0;

		default:
			// For unknown nodes, require at least one connection
			return incomingEdges.length > 0 || outgoingEdges.length > 0;
	}
}

/**
 * Check if a node is considered complete (configured and connected)
 * @param node - Node to validate
 * @param edges - Edges in the workflow
 * @param isDraft - Whether this is a draft workflow (more lenient validation)
 */
export function isNodeComplete(node: CustomNode, edges: Edge[], isDraft: boolean = true): boolean {
	return hasValidConfiguration(node, isDraft) && hasRequiredConnections(node, edges);
}

/**
 * Validate a complete workflow for deprecated patterns and required structure
 */
export function validateWorkflow(workflow: {
	nodes: CustomNode[];
	edges: Edge[];
	submissionForm?: any;
}): string[] {
	const errors: string[] = [];

	// ❌ Reject start nodes - they are deprecated
	const startNodes = workflow.nodes?.filter(node => node.type === 'start');
	if (startNodes && startNodes.length > 0) {
		errors.push(
			'Start nodes are deprecated. Remove them and connect submissionForm directly to processing nodes.'
		);
	}

	// ✅ Must have submissionForm for workflow trigger
	if (!workflow.submissionForm) {
		errors.push('Workflow must have a submissionForm to serve as the trigger.');
	}

	// ✅ Must have at least one processing node connected to submissionForm
	if (workflow.submissionForm && workflow.edges) {
		const connectedFromForm = workflow.edges.some(
			edge => edge.source === workflow.submissionForm?.id
		);
		if (!connectedFromForm && workflow.nodes.length > 0) {
			errors.push('SubmissionForm must connect to at least one processing node.');
		}
	}

	return errors;
}

/**
 * Check if a workflow uses deprecated patterns
 */
export function hasDeprecatedPatterns(workflow: { nodes: CustomNode[] }): boolean {
	return workflow.nodes?.some(node => node.type === 'start') || false;
}

/**
 * Get completion status details for a node
 * @param node - Node to validate
 * @param edges - Edges in the workflow
 * @param isDraft - Whether this is a draft workflow (more lenient validation)
 */
export function getNodeCompletionStatus(
	node: CustomNode,
	edges: Edge[],
	isDraft: boolean = true
): {
	isComplete: boolean;
	hasConfiguration: boolean;
	hasConnections: boolean;
	missingRequirements: string[];
} {
	const hasConfiguration = hasValidConfiguration(node, isDraft);
	const hasConnections = hasRequiredConnections(node, edges);
	const isComplete = hasConfiguration && hasConnections;

	const missingRequirements: string[] = [];
	if (!hasConfiguration) {
		missingRequirements.push('configuration');
	}
	if (!hasConnections) {
		missingRequirements.push('connections');
	}

	return {
		isComplete,
		hasConfiguration,
		hasConnections,
		missingRequirements,
	};
}
